import { yupResolver } from "@hookform/resolvers/yup";
import { Form, RadioField, TextAreaField } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import React from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { useAirRevenueSourceUpdateMutation } from "../../../../queries/air-response/air-revenue-source.mutations";
import { COMMON_MESSAGES } from "../../../common-messages";
import { MESSAGES } from "../message-definitions";

import type { Step } from "./constants";
import { isNoOtherConnectionsReasonCode } from "./constants";

interface NoPlatformReasonFormProps {
  setQuestionaireStep: (step: Step) => void;
  airPublicIdentifier: string;
  onSubmit: () => void;
}

export const NoPlatformReasonForm = ({
  setQuestionaireStep,
  airPublicIdentifier,
  onSubmit,
}: NoPlatformReasonFormProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const updateAirRevenueSource = useAirRevenueSourceUpdateMutation(customerDomain);
  type NoPlatformFormFields = {
    noPlatformReason: string;
    otherReason?: string;
  };

  const schema = Yup.object().shape({
    noPlatformReason: Yup.string().required(intl.formatMessage(MESSAGES.pleaseSelectAnOptionError)),
    otherReason: Yup.string().when("noPlatformReason", {
      is: "other",
      then: (schema) => schema.required(intl.formatMessage(MESSAGES.pleaseSpecifyReasonError)),
    }),
  });
  const form = useForm<NoPlatformFormFields>({
    resolver: yupResolver(schema),
  });
  const {
    formState: { errors: errors },
  } = form;

  const noPlatformReasonSubmitDisabled = (() => {
    const { noPlatformReason, otherReason } = form.getValues();
    if (noPlatformReason === "other") {
      return otherReason === undefined || otherReason === "";
    }
    return noPlatformReason === undefined || noPlatformReason === "";
  })();

  const questionaire: {
    value: FundingApplicationSchemas.NoOtherConnectionsReasonCode;
    label: string;
    associatedField?: React.ReactNode;
  }[] = [
    { value: "company_less_than_6m_old", label: intl.formatMessage(MESSAGES.lessThan180DaysCompanyOption) },
    {
      value: "store_decommissioned",
      label: intl.formatMessage(MESSAGES.previousCompantDecommissionedOption),
    },
    { value: "no_other_revenue_sources", label: intl.formatMessage(MESSAGES.noOtherPlatformsOption) },
    {
      value: "other",
      label: intl.formatMessage(MESSAGES.otherReasonOption),
      associatedField: (
        <TextAreaField
          name="otherReason"
          allowVerticalResize
          label={intl.formatMessage(MESSAGES.pleaseSpecifyReason)}
          error={errors.otherReason?.message}
          // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
          defaultValue={form.watch("otherReason") ?? ""}
          onChange={({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
            form.setValue("otherReason", target.value);
          }}
        />
      ),
    },
  ];

  const onNoPlatformReasonSubmit = (formData: NoPlatformFormFields) => {
    updateAirRevenueSource
      .mutateAsync({
        airPublicId: airPublicIdentifier,
        // If noPlatformReason is "other", we need to pass the otherReason as the noOtherConnectionsReason
        noOtherConnectionsReason: isNoOtherConnectionsReasonCode(formData.noPlatformReason)
          ? (formData.noPlatformReason as FundingApplicationSchemas.NoOtherConnectionsReasonCode)
          : undefined,
        reason: formData.otherReason !== "" ? formData.otherReason : undefined,
        connectionIds: [],
      })
      .then(() => {
        onSubmit();
      });
  };

  return (
    <Form
      onSubmit={form.handleSubmit(onNoPlatformReasonSubmit)}
      primaryAction={{
        content: intl.formatMessage(COMMON_MESSAGES.submitLabel),
        analyticsId: "revenue-air-no-platform-reason-submit-button",
        disabled: noPlatformReasonSubmitDisabled,
      }}
      secondaryAction={{
        onAction: () => setQuestionaireStep("has_additional_platform"),
        content: intl.formatMessage(COMMON_MESSAGES.backLabel),
        analyticsId: "revenue-air-no-platform-reason-back-button",
      }}
    >
      <RadioField
        name="noPlatformReason"
        error={errors.noPlatformReason?.message}
        defaultValue={form.watch("noPlatformReason") ?? null}
        label={intl.formatMessage(MESSAGES.noOtherPlatformsLabel)}
        type="block"
        options={questionaire}
        onChange={(value) => {
          form.setValue("otherReason", "");
          form.setValue("noPlatformReason", value as string);
        }}
      />
    </Form>
  );
};
